import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Dialog,
  DataTable,
  Field,
  Grid,
  Inline,
  KpiCard,
  Panel,
  SegmentedControl,
  SelectField,
  Stack,
  StatusBadge,
  TextAreaField,
  majorInputToMinor,
  useToast
} from "@leylion/design-system";
import {
  approveOperatingExpense,
  changeFinancialPeriodStatus,
  createManualJournal,
  getBalanceSheet,
  getExpenseCategories,
  getFinanceAccounts,
  getFinancialPeriods,
  getGeneralLedger,
  getJournalEntries,
  getOperatingExpenses,
  getProfitAndLoss,
  getTrialBalance,
  postJournal,
  postOperatingExpense,
  rebuildFinancePostings,
  reverseJournal,
  saveExpenseCategory,
  saveFinanceAccount,
  saveFinancialPeriod,
  saveOperatingExpense
} from "../api/client";
import type {
  BalanceSheet,
  ExpenseCategory,
  FinanceAccount,
  FinancialPeriod,
  GeneralLedger,
  JournalEntry,
  OperatingExpense,
  ProfitAndLoss,
  ReportScope,
  TrialBalance
} from "../api/reports-types";
import {
  DynamicReportTable,
  LoadingReport,
  ReportError,
  SectionHeading,
  dateLabel,
  dateTimeLabel,
  money,
  quantity,
  statusTone,
  type ReportsLanguage
} from "./reports-components";

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Unexpected error.";
}

type FinanceSection =
  | "statements"
  | "ledger"
  | "journals"
  | "expenses"
  | "accounts"
  | "periods";

export function FinanceWorkspace({
  language,
  scope,
  refreshKey,
  permissions,
  onChanged
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
  permissions: string[];
  onChanged: () => void;
}) {
  const ar = language === "ar";
  const [section, setSection] =
    useState<FinanceSection>("statements");
  const canManage = permissions.includes("finance.manage");
  const canPost = permissions.includes("finance.journal.post");
  const canExpenses =
    permissions.includes("finance.expenses.manage");
  const canClose =
    permissions.includes("finance.period.close");

  return (
    <Stack>
      <SegmentedControl
        value={section}
        onChange={setSection}
        label={ar ? "المركز المالي" : "Finance workspace"}
        options={[
          {
            value: "statements",
            label: ar ? "القوائم المالية" : "Statements"
          },
          {
            value: "ledger",
            label: ar ? "دفتر الأستاذ" : "Ledger"
          },
          {
            value: "journals",
            label: ar ? "القيود" : "Journals"
          },
          {
            value: "expenses",
            label: ar ? "المصروفات" : "Expenses"
          },
          {
            value: "accounts",
            label: ar ? "دليل الحسابات" : "Accounts"
          },
          {
            value: "periods",
            label: ar ? "الفترات" : "Periods"
          }
        ]}
      />

      {section === "statements" ? (
        <StatementsSection
          language={language}
          scope={scope}
          refreshKey={refreshKey}
        />
      ) : null}
      {section === "ledger" ? (
        <LedgerSection
          language={language}
          scope={scope}
          refreshKey={refreshKey}
        />
      ) : null}
      {section === "journals" ? (
        <JournalsSection
          key={scope.currency}
          language={language}
          currency={scope.currency}
          refreshKey={refreshKey}
          canManage={canManage}
          canPost={canPost}
          onChanged={onChanged}
        />
      ) : null}
      {section === "expenses" ? (
        <ExpensesSection
          key={scope.currency}
          language={language}
          currency={scope.currency}
          refreshKey={refreshKey}
          canManage={canExpenses}
          canPost={canPost}
          onChanged={onChanged}
        />
      ) : null}
      {section === "accounts" ? (
        <AccountsSection
          key={scope.currency}
          currency={scope.currency}
          language={language}
          refreshKey={refreshKey}
          canManage={canManage}
          onChanged={onChanged}
        />
      ) : null}
      {section === "periods" ? (
        <PeriodsSection
          language={language}
          refreshKey={refreshKey}
          canManage={canManage}
          canClose={canClose}
          onChanged={onChanged}
        />
      ) : null}
    </Stack>
  );
}

function StatementsSection({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const [profitLoss, setProfitLoss] =
    useState<ProfitAndLoss | null>(null);
  const [balanceSheet, setBalanceSheet] =
    useState<BalanceSheet | null>(null);
  const [trialBalance, setTrialBalance] =
    useState<TrialBalance | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let active = true;
    setBusy(true);
    setError("");
    Promise.all([
      getProfitAndLoss(scope),
      getBalanceSheet(scope),
      getTrialBalance(scope)
    ])
      .then(([pnl, balance, trial]) => {
        if (!active) return;
        setProfitLoss(pnl);
        setBalanceSheet(balance);
        setTrialBalance(trial);
      })
      .catch((caught) => {
        if (active) setError(errorMessage(caught));
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [JSON.stringify(scope), refreshKey]);

  if (busy || !profitLoss || !balanceSheet || !trialBalance) {
    return error
      ? <ReportError language={language} error={error} />
      : <LoadingReport language={language} />;
  }

  return (
    <Stack>
      <Grid columns={4}>
        <KpiCard
          label={ar ? "الإيرادات" : "Revenue"}
          value={money(
            profitLoss.revenueMinor,
            language,
            scope.currency
          )}
        />
        <KpiCard
          label={ar ? "الربح الإجمالي" : "Gross profit"}
          value={money(
            profitLoss.grossProfitMinor,
            language,
            scope.currency
          )}
          trendTone={
            profitLoss.grossProfitMinor >= 0
              ? "success"
              : "danger"
          }
        />
        <KpiCard
          label={ar ? "صافي الربح" : "Net profit"}
          value={money(
            profitLoss.netProfitMinor,
            language,
            scope.currency
          )}
          trendTone={
            profitLoss.netProfitMinor >= 0
              ? "success"
              : "danger"
          }
        />
        <KpiCard
          label={ar ? "ميزان المراجعة" : "Trial balance"}
          value={
            trialBalance.isBalanced
              ? ar ? "متوازن" : "Balanced"
              : ar ? "غير متوازن" : "Unbalanced"
          }
          trendTone={
            trialBalance.isBalanced ? "success" : "danger"
          }
        />
      </Grid>

      <div className="reports-statements">
        <StatementCard
          language={language}
          title={ar ? "قائمة الدخل" : "Profit & Loss"}
          lines={profitLoss.lines}
          currency={scope.currency}
        />
        <StatementCard
          language={language}
          title={ar ? "الأصول" : "Assets"}
          lines={balanceSheet.assets}
          currency={scope.currency}
          total={balanceSheet.totalAssetsMinor}
        />
        <StatementCard
          language={language}
          title={ar ? "الالتزامات وحقوق الملكية" : "Liabilities & Equity"}
          lines={[
            ...balanceSheet.liabilities,
            ...balanceSheet.equity
          ]}
          currency={scope.currency}
          total={
            balanceSheet.totalLiabilitiesMinor
            + balanceSheet.totalEquityMinor
          }
        />
      </div>

      <Alert
        tone={balanceSheet.isBalanced ? "success" : "danger"}
        title={
          balanceSheet.isBalanced
            ? ar ? "الميزانية متوازنة" : "Balance sheet is balanced"
            : ar ? "الميزانية غير متوازنة" : "Balance sheet is not balanced"
        }
      >
        {ar
          ? `الأصول ${money(balanceSheet.totalAssetsMinor, language, scope.currency)} مقابل الالتزامات وحقوق الملكية ${money(balanceSheet.totalLiabilitiesMinor + balanceSheet.totalEquityMinor, language, scope.currency)}.`
          : `Assets ${money(balanceSheet.totalAssetsMinor, language, scope.currency)} versus liabilities and equity ${money(balanceSheet.totalLiabilitiesMinor + balanceSheet.totalEquityMinor, language, scope.currency)}.`}
      </Alert>
    </Stack>
  );
}

function StatementCard({
  language,
  title,
  lines,
  currency,
  total
}: {
  language: ReportsLanguage;
  title: string;
  lines: Array<{
    code: string;
    labelAr: string;
    labelEn: string;
    currentMinor: number;
    isTotal: boolean;
  }>;
  currency: string;
  total?: number;
}) {
  return (
    <Panel title={title}>
      <div className="reports-statement-lines">
        {lines.map((line) => (
          <div
            key={`${title}-${line.code}`}
            className={line.isTotal ? "is-total" : ""}
          >
            <span>
              <small>{line.code}</small>
              {language === "ar" ? line.labelAr : line.labelEn}
            </span>
            <strong>
              {money(line.currentMinor, language, currency)}
            </strong>
          </div>
        ))}
        {total != null ? (
          <div className="is-grand-total">
            <strong>
              {language === "ar" ? "الإجمالي" : "Total"}
            </strong>
            <strong>{money(total, language, currency)}</strong>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}

function LedgerSection({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const [ledger, setLedger] = useState<GeneralLedger | null>(null);
  const [accounts, setAccounts] = useState<FinanceAccount[]>([]);
  const [accountId, setAccountId] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    void getFinanceAccounts()
      .then(setAccounts)
      .catch((caught) => setError(errorMessage(caught)));
  }, [refreshKey]);

  useEffect(() => {
    let active = true;
    setBusy(true);
    getGeneralLedger(scope, accountId || undefined)
      .then((value) => {
        if (active) setLedger(value);
      })
      .catch((caught) => {
        if (active) setError(errorMessage(caught));
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [JSON.stringify(scope), refreshKey, accountId]);

  if (busy || !ledger) {
    return error
      ? <ReportError language={language} error={error} />
      : <LoadingReport language={language} />;
  }

  const table = {
    columns: [
      "postingDate",
      "entryNumber",
      "description",
      "accountCode",
      "accountName",
      "debitMinor",
      "creditMinor",
      "runningBalanceMinor",
      "sourceReference"
    ],
    rows: ledger.lines.map((line) => ({
      postingDate: line.postingDate,
      entryNumber: line.entryNumber,
      description: line.description,
      accountCode: line.accountCode,
      accountName: line.accountName,
      debitMinor: line.debitMinor,
      creditMinor: line.creditMinor,
      runningBalanceMinor: line.runningBalanceMinor,
      sourceReference: line.sourceReference
    })),
    totals: {
      debitMinor: ledger.totalDebitMinor,
      creditMinor: ledger.totalCreditMinor
    }
  };

  return (
    <Stack>
      {error ? (
        <ReportError language={language} error={error} />
      ) : null}
      <Panel>
        <div className="reports-ledger-filter">
          <SelectField
            label={ar ? "الحساب" : "Account"}
            value={accountId}
            onChange={(event) => setAccountId(event.target.value)}
          >
            <option value="">
              {ar ? "جميع الحسابات" : "All accounts"}
            </option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.code} · {ar ? account.nameAr : account.nameEn}
              </option>
            ))}
          </SelectField>
          <div className="reports-ledger-totals">
            <span>
              {ar ? "إجمالي المدين" : "Total debit"}
              <strong>
                {money(ledger.totalDebitMinor, language, scope.currency)}
              </strong>
            </span>
            <span>
              {ar ? "إجمالي الدائن" : "Total credit"}
              <strong>
                {money(ledger.totalCreditMinor, language, scope.currency)}
              </strong>
            </span>
          </div>
        </div>
      </Panel>
      <DynamicReportTable
        language={language}
        title={ar ? "دفتر الأستاذ العام" : "General ledger"}
        table={table}
        currency={scope.currency}
      />
    </Stack>
  );
}

function JournalsSection({
  language,
  currency,
  refreshKey,
  canManage,
  canPost,
  onChanged
}: {
  language: ReportsLanguage;
  currency: string;
  refreshKey: number;
  canManage: boolean;
  canPost: boolean;
  onChanged: () => void;
}) {
  const ar = language === "ar";
  const toast = useToast();
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [accounts, setAccounts] = useState<FinanceAccount[]>([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [postingDate, setPostingDate] =
    useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState("");
  const [lines, setLines] = useState([
    {
      accountId: "",
      debit: "",
      credit: "",
      description: ""
    },
    {
      accountId: "",
      debit: "",
      credit: "",
      description: ""
    }
  ]);
  const [reverseTarget, setReverseTarget] =
    useState<JournalEntry | null>(null);
  const [reverseReason, setReverseReason] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    const [journalRows, accountRows] = await Promise.all([
      getJournalEntries({
        currency,
        status: status || undefined,
        search: search || undefined
      }),
      getFinanceAccounts(currency)
    ]);
    setEntries(journalRows);
    setAccounts(accountRows);
  };

  useEffect(() => {
    void load().catch((caught) => setError(errorMessage(caught)));
  }, [refreshKey, status]);

  const journalAmountsValid = useMemo(
    () => lines.every((line) =>
      (!line.debit.trim() || majorInputToMinor(line.debit, currency) !== null)
      && (!line.credit.trim() || majorInputToMinor(line.credit, currency) !== null)
    ),
    [lines, currency]
  );

  const totals = useMemo(() => ({
    debitMinor: lines.reduce(
      (sum, line) => sum + (majorInputToMinor(line.debit || "0", currency) ?? 0),
      0
    ),
    creditMinor: lines.reduce(
      (sum, line) => sum + (majorInputToMinor(line.credit || "0", currency) ?? 0),
      0
    )
  }), [lines]);

  const create = async () => {
    if (!journalAmountsValid) {
      setError(ar ? "أدخل مبالغ صحيحة للمدين والدائن." : "Enter valid debit and credit amounts.");
      return;
    }
    try {
      const prepared = lines
        .filter((line) => line.accountId)
        .map((line) => ({
          accountId: line.accountId,
          debitMinor: majorInputToMinor(line.debit || "0", currency) ?? 0,
          creditMinor: majorInputToMinor(line.credit || "0", currency) ?? 0,
          description: line.description || null,
          counterpartyType: null,
          counterpartyId: null,
          costCenterCode: null
        }));
      await createManualJournal({
        postingDate,
        currency,
        description,
        lines: prepared
      });
      setEditorOpen(false);
      setDescription("");
      setLines([
        { accountId: "", debit: "", credit: "", description: "" },
        { accountId: "", debit: "", credit: "", description: "" }
      ]);
      await load();
      onChanged();
      toast.notify({
        tone: "success",
        title: ar ? "تم إنشاء القيد" : "Journal created"
      });
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const post = async (entry: JournalEntry) => {
    try {
      await postJournal(entry);
      await load();
      onChanged();
      toast.notify({
        tone: "success",
        title: ar ? "تم ترحيل القيد" : "Journal posted"
      });
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const reverse = async () => {
    if (!reverseTarget || !reverseReason) return;
    try {
      await reverseJournal(
        reverseTarget,
        new Date().toISOString().slice(0, 10),
        reverseReason
      );
      setReverseTarget(null);
      setReverseReason("");
      await load();
      onChanged();
      toast.notify({
        tone: "success",
        title: ar ? "تم عكس القيد" : "Journal reversed"
      });
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  return (
    <Stack>
      {error ? (
        <ReportError language={language} error={error} />
      ) : null}
      <Panel
        title={ar ? "القيود اليومية" : "Journal entries"}
        actions={
          canManage ? (
            <Button
              data-testid="new-manual-journal"
              onClick={() => setEditorOpen(true)}
            >
              {ar ? "قيد يدوي" : "Manual journal"}
            </Button>
          ) : null
        }
      >
        <div className="reports-filter-row">
          <Field
            label={ar ? "بحث" : "Search"}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                void load();
              }
            }}
          />
          <SelectField
            label={ar ? "الحالة" : "Status"}
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">{ar ? "الكل" : "All"}</option>
            <option value="Draft">Draft</option>
            <option value="Posted">Posted</option>
            <option value="Reversed">Reversed</option>
          </SelectField>
          <Button variant="secondary" onClick={() => void load()}>
            {ar ? "تطبيق" : "Apply"}
          </Button>
        </div>
      </Panel>

      <Panel>
        <div className="reports-dynamic-table-wrap">
          <table className="reports-dynamic-table">
            <thead>
              <tr>
                <th>{ar ? "القيد" : "Entry"}</th>
                <th>{ar ? "التاريخ" : "Date"}</th>
                <th>{ar ? "الوصف" : "Description"}</th>
                <th>{ar ? "المصدر" : "Source"}</th>
                <th>{ar ? "المدين" : "Debit"}</th>
                <th>{ar ? "الدائن" : "Credit"}</th>
                <th>{ar ? "الحالة" : "Status"}</th>
                <th>{ar ? "إجراءات" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    <strong>{entry.entryNumber}</strong>
                    <small className="reports-cell-sub">
                      {entry.sourceReference ?? "—"}
                    </small>
                  </td>
                  <td>{dateLabel(entry.postingDate, language)}</td>
                  <td>{entry.description}</td>
                  <td>{entry.sourceType}</td>
                  <td>{money(entry.totalDebitMinor, language, entry.currency)}</td>
                  <td>{money(entry.totalCreditMinor, language, entry.currency)}</td>
                  <td>
                    <StatusBadge tone={statusTone(entry.status)}>
                      {entry.status}
                    </StatusBadge>
                  </td>
                  <td>
                    <Inline>
                      {canPost && entry.status === "Draft" ? (
                        <Button
                          size="sm"
                          data-testid={`post-journal-${entry.id}`}
                          onClick={() => void post(entry)}
                        >
                          {ar ? "ترحيل" : "Post"}
                        </Button>
                      ) : null}
                      {canPost && entry.status === "Posted" ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setReverseTarget(entry)}
                        >
                          {ar ? "عكس" : "Reverse"}
                        </Button>
                      ) : null}
                    </Inline>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Dialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={ar ? "قيد يومية يدوي" : "Manual journal entry"}
        description={
          ar
            ? "يجب أن يتساوى إجمالي المدين والدائن."
            : "Debit and credit totals must be equal."
        }
        footer={
          <Inline>
            <Button
              variant="secondary"
              onClick={() => setEditorOpen(false)}
            >
              {ar ? "إلغاء" : "Cancel"}
            </Button>
            <Button
              data-testid="save-manual-journal"
              disabled={
                !description
                || !journalAmountsValid
                || totals.debitMinor <= 0
                || totals.debitMinor !== totals.creditMinor
              }
              onClick={() => void create()}
            >
              {ar ? "حفظ كمسودة" : "Save draft"}
            </Button>
          </Inline>
        }
      >
        <Stack>
          <Grid columns={2}>
            <Field
              label={ar ? "تاريخ القيد" : "Posting date"}
              type="date"
              value={postingDate}
              onChange={(event) => setPostingDate(event.target.value)}
            />
            <Field
              label={ar ? "الوصف" : "Description"}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Grid>
          <div className="reports-journal-lines">
            {lines.map((line, index) => (
              <div className="reports-journal-line" key={index}>
                <SelectField
                  label={ar ? "الحساب" : "Account"}
                  value={line.accountId}
                  onChange={(event) => {
                    const next = [...lines];
                    next[index] = {
                      ...line,
                      accountId: event.target.value
                    };
                    setLines(next);
                  }}
                >
                  <option value="">
                    {ar ? "اختر الحساب" : "Select account"}
                  </option>
                  {accounts
                    .filter((account) =>
                      account.isActive && account.allowManualPosting)
                    .map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.code} · {ar ? account.nameAr : account.nameEn}
                      </option>
                    ))}
                </SelectField>
                <Field
                  label={ar ? "مدين" : "Debit"}
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.debit}
                  onChange={(event) => {
                    const next = [...lines];
                    next[index] = {
                      ...line,
                      debit: event.target.value,
                      credit: event.target.value ? "" : line.credit
                    };
                    setLines(next);
                  }}
                />
                <Field
                  label={ar ? "دائن" : "Credit"}
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.credit}
                  onChange={(event) => {
                    const next = [...lines];
                    next[index] = {
                      ...line,
                      credit: event.target.value,
                      debit: event.target.value ? "" : line.debit
                    };
                    setLines(next);
                  }}
                />
                <Field
                  label={ar ? "بيان السطر" : "Line description"}
                  value={line.description}
                  onChange={(event) => {
                    const next = [...lines];
                    next[index] = {
                      ...line,
                      description: event.target.value
                    };
                    setLines(next);
                  }}
                />
                {lines.length > 2 ? (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setLines(
                      lines.filter((_, itemIndex) => itemIndex !== index)
                    )}
                  >
                    {ar ? "حذف" : "Remove"}
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
          <Button
            variant="secondary"
            onClick={() => setLines([
              ...lines,
              { accountId: "", debit: "", credit: "", description: "" }
            ])}
          >
            {ar ? "إضافة سطر" : "Add line"}
          </Button>
          <div className="reports-journal-balance">
            <span>
              {ar ? "المدين" : "Debit"}:
              <strong>{money(totals.debitMinor, language, currency)}</strong>
            </span>
            <span>
              {ar ? "الدائن" : "Credit"}:
              <strong>{money(totals.creditMinor, language, currency)}</strong>
            </span>
            <StatusBadge
              tone={
                totals.debitMinor > 0 && totals.debitMinor === totals.creditMinor
                  ? "success"
                  : "warning"
              }
            >
              {totals.debitMinor > 0 && totals.debitMinor === totals.creditMinor
                ? ar ? "متوازن" : "Balanced"
                : ar ? "غير متوازن" : "Unbalanced"}
            </StatusBadge>
          </div>
        </Stack>
      </Dialog>

      <Dialog
        open={Boolean(reverseTarget)}
        onClose={() => setReverseTarget(null)}
        title={ar ? "عكس القيد" : "Reverse journal"}
        footer={
          <Inline>
            <Button
              variant="secondary"
              onClick={() => setReverseTarget(null)}
            >
              {ar ? "إلغاء" : "Cancel"}
            </Button>
            <Button
              variant="danger"
              disabled={!reverseReason}
              onClick={() => void reverse()}
            >
              {ar ? "تأكيد العكس" : "Confirm reversal"}
            </Button>
          </Inline>
        }
      >
        <TextAreaField
          label={ar ? "سبب العكس" : "Reversal reason"}
          value={reverseReason}
          onChange={(event) => setReverseReason(event.target.value)}
        />
      </Dialog>
    </Stack>
  );
}

function ExpensesSection({
  language,
  currency,
  refreshKey,
  canManage,
  canPost,
  onChanged
}: {
  language: ReportsLanguage;
  currency: string;
  refreshKey: number;
  canManage: boolean;
  canPost: boolean;
  onChanged: () => void;
}) {
  const ar = language === "ar";
  const toast = useToast();
  const [expenses, setExpenses] = useState<OperatingExpense[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [accounts, setAccounts] = useState<FinanceAccount[]>([]);
  const [categoryEditor, setCategoryEditor] = useState<Partial<ExpenseCategory> | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const [expenseDate, setExpenseDate] =
    useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState("");
  const [vendor, setVendor] = useState("");
  const [invoice, setInvoice] = useState("");
  const [net, setNet] = useState("");
  const [tax, setTax] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [paymentReference, setPaymentReference] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    const [expenseRows, categoryRows, accountRows] = await Promise.all([
      getOperatingExpenses({ currency }),
      getExpenseCategories(),
      getFinanceAccounts(currency)
    ]);
    setExpenses(expenseRows);
    setCategories(categoryRows);
    setAccounts(accountRows);
  };

  useEffect(() => {
    void load().catch((caught) => setError(errorMessage(caught)));
  }, [refreshKey]);

  const expenseAmountsValid =
    (!net.trim() || majorInputToMinor(net, currency) !== null)
    && (!tax.trim() || majorInputToMinor(tax, currency) !== null);

  const save = async () => {
    const netMinor = majorInputToMinor(net || "0", currency);
    const taxMinor = majorInputToMinor(tax || "0", currency);
    if (netMinor === null || taxMinor === null || netMinor + taxMinor <= 0) {
      setError(ar ? "أدخل مبالغ صحيحة للمصروف." : "Enter valid expense amounts.");
      return;
    }
    try {
      await saveOperatingExpense({
        categoryId,
        expenseDate,
        description,
        vendorName: vendor || null,
        vendorTaxRegistrationNumber: null,
        supplierInvoiceNumber: invoice || null,
        currency,
        netMinor,
        taxMinor,
        paymentMethod,
        paymentReference: paymentReference || null
      });
      setEditorOpen(false);
      setCategoryId("");
      setDescription("");
      setVendor("");
      setInvoice("");
      setNet("");
      setTax("");
      setPaymentReference("");
      await load();
      onChanged();
      toast.notify({
        tone: "success",
        title: ar ? "تم حفظ المصروف" : "Expense saved"
      });
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const saveCategory = async () => {
    if (!categoryEditor?.expenseAccountId || !categoryEditor.code || !categoryEditor.nameAr || !categoryEditor.nameEn) return;
    try {
      await saveExpenseCategory({
        id: categoryEditor.id,
        expenseAccountId: categoryEditor.expenseAccountId,
        code: categoryEditor.code,
        nameAr: categoryEditor.nameAr,
        nameEn: categoryEditor.nameEn,
        isActive: categoryEditor.isActive ?? true,
        version: categoryEditor.version
      });
      setCategoryEditor(null);
      await load();
      onChanged();
      toast.notify({ tone: "success", title: ar ? "تم حفظ فئة المصروف" : "Expense category saved" });
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const approve = async (expense: OperatingExpense) => {
    try {
      await approveOperatingExpense(expense);
      await load();
      onChanged();
      toast.notify({
        tone: "success",
        title: ar ? "تم اعتماد المصروف" : "Expense approved"
      });
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const post = async (expense: OperatingExpense) => {
    try {
      await postOperatingExpense(expense);
      await load();
      onChanged();
      toast.notify({
        tone: "success",
        title: ar ? "تم ترحيل المصروف" : "Expense posted"
      });
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const draftTotal = expenses
    .filter((expense) => expense.status === "Draft")
    .reduce((sum, expense) => sum + expense.totalMinor, 0);
  const postedTotal = expenses
    .filter((expense) => expense.status === "Posted")
    .reduce((sum, expense) => sum + expense.totalMinor, 0);

  return (
    <Stack>
      {error ? <ReportError language={language} error={error} /> : null}
      <Grid columns={3}>
        <KpiCard
          label={ar ? "مصروفات مسودة" : "Draft expenses"}
          value={money(draftTotal, language, currency)}
          trendTone={draftTotal > 0 ? "warning" : "success"}
        />
        <KpiCard
          label={ar ? "مصروفات مرحلة" : "Posted expenses"}
          value={money(postedTotal, language, currency)}
        />
        <KpiCard
          label={ar ? "عدد السجلات" : "Expense records"}
          value={expenses.length}
        />
      </Grid>
      <Panel
        title={ar ? "فئات المصروفات" : "Expense categories"}
        actions={canManage ? <Button data-testid="new-expense-category" onClick={() => setCategoryEditor({ code: "", nameAr: "", nameEn: "", expenseAccountId: accounts.find((account) => account.accountType === "Expense" && account.isActive)?.id ?? "", isActive: true })}>{ar ? "فئة جديدة" : "New category"}</Button> : null}
      >
        <DataTable
          rows={categories}
          rowKey={(category) => category.id}
          emptyTitle={ar ? "لا توجد فئات مصروفات" : "No expense categories"}
          columns={[
            { key: "code", header: ar ? "الرمز" : "Code", cell: (category) => <code>{category.code}</code> },
            { key: "name", header: ar ? "الفئة" : "Category", cell: (category) => ar ? category.nameAr : category.nameEn },
            { key: "account", header: ar ? "حساب المصروف" : "Expense account", cell: (category) => category.expenseAccountCode },
            { key: "status", header: ar ? "الحالة" : "Status", cell: (category) => <StatusBadge tone={category.isActive ? "success" : "neutral"}>{category.isActive ? (ar ? "نشطة" : "Active") : (ar ? "متوقفة" : "Inactive")}</StatusBadge> },
            { key: "action", header: "", cell: (category) => canManage ? <Button size="sm" variant="secondary" onClick={() => setCategoryEditor(category)}>{ar ? "تعديل" : "Edit"}</Button> : null }
          ]}
        />
      </Panel>

      <Panel
        title={ar ? "المصروفات التشغيلية" : "Operating expenses"}
        actions={
          canManage ? (
            <Button
              data-testid="new-operating-expense"
              onClick={() => setEditorOpen(true)}
            >
              {ar ? "مصروف جديد" : "New expense"}
            </Button>
          ) : null
        }
      >
        <div className="reports-dynamic-table-wrap">
          <table className="reports-dynamic-table">
            <thead>
              <tr>
                <th>{ar ? "السند" : "Expense"}</th>
                <th>{ar ? "التاريخ" : "Date"}</th>
                <th>{ar ? "الفئة" : "Category"}</th>
                <th>{ar ? "الوصف" : "Description"}</th>
                <th>{ar ? "المورد" : "Vendor"}</th>
                <th>{ar ? "الصافي" : "Net"}</th>
                <th>{ar ? "الضريبة" : "Tax"}</th>
                <th>{ar ? "الإجمالي" : "Total"}</th>
                <th>{ar ? "الحالة" : "Status"}</th>
                <th>{ar ? "إجراء" : "Action"}</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((expense) => (
                <tr key={expense.id}>
                  <td>
                    <strong>{expense.expenseNumber}</strong>
                    <small className="reports-cell-sub">
                      {expense.supplierInvoiceNumber ?? "—"}
                    </small>
                  </td>
                  <td>{dateLabel(expense.expenseDate, language)}</td>
                  <td>{expense.categoryName}</td>
                  <td>{expense.description}</td>
                  <td>{expense.vendorName ?? "—"}</td>
                  <td>{money(expense.netMinor, language, expense.currency)}</td>
                  <td>{money(expense.taxMinor, language, expense.currency)}</td>
                  <td>{money(expense.totalMinor, language, expense.currency)}</td>
                  <td>
                    <StatusBadge tone={statusTone(expense.status)}>
                      {expense.status}
                    </StatusBadge>
                  </td>
                  <td>
                    <Inline>
                      {canManage && expense.status === "Draft" ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => void approve(expense)}
                        >
                          {ar ? "اعتماد" : "Approve"}
                        </Button>
                      ) : null}
                      {canPost && expense.status === "Approved" ? (
                        <Button
                          size="sm"
                          data-testid={`post-expense-${expense.id}`}
                          onClick={() => void post(expense)}
                        >
                          {ar ? "ترحيل" : "Post"}
                        </Button>
                      ) : null}
                    </Inline>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Dialog
        open={Boolean(categoryEditor)}
        onClose={() => setCategoryEditor(null)}
        title={ar ? "تعريف فئة المصروف" : "Expense category definition"}
        footer={<Inline><Button variant="secondary" onClick={() => setCategoryEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button data-testid="save-expense-category" disabled={!categoryEditor?.expenseAccountId || !categoryEditor.code || !categoryEditor.nameAr || !categoryEditor.nameEn} onClick={() => void saveCategory()}>{ar ? "حفظ" : "Save"}</Button></Inline>}
      >
        {categoryEditor ? <Stack><Grid columns={2}><SelectField data-testid="expense-category-account" label={ar ? "حساب المصروف" : "Expense account"} value={categoryEditor.expenseAccountId ?? ""} onChange={(event) => setCategoryEditor({ ...categoryEditor, expenseAccountId: event.target.value })}><option value="">{ar ? "اختر الحساب" : "Select account"}</option>{accounts.filter((account) => account.accountType === "Expense" && account.isActive).map((account) => <option key={account.id} value={account.id}>{account.code} · {ar ? account.nameAr : account.nameEn}</option>)}</SelectField><Field label={ar ? "الرمز" : "Code"} value={categoryEditor.code ?? ""} onChange={(event) => setCategoryEditor({ ...categoryEditor, code: event.target.value.toUpperCase() })} /><Field label={ar ? "الاسم العربي" : "Arabic name"} value={categoryEditor.nameAr ?? ""} onChange={(event) => setCategoryEditor({ ...categoryEditor, nameAr: event.target.value })} /><Field label={ar ? "الاسم الإنجليزي" : "English name"} value={categoryEditor.nameEn ?? ""} onChange={(event) => setCategoryEditor({ ...categoryEditor, nameEn: event.target.value })} /></Grid><Checkbox label={ar ? "نشطة" : "Active"} checked={categoryEditor.isActive ?? true} onChange={(event) => setCategoryEditor({ ...categoryEditor, isActive: event.target.checked })} /></Stack> : null}
      </Dialog>

      <Dialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={ar ? "مصروف تشغيلي جديد" : "New operating expense"}
        footer={
          <Inline>
            <Button
              variant="secondary"
              onClick={() => setEditorOpen(false)}
            >
              {ar ? "إلغاء" : "Cancel"}
            </Button>
            <Button
              data-testid="save-operating-expense"
              disabled={
                !categoryId
                || !description
                || !expenseAmountsValid
                || (majorInputToMinor(net || "0", currency) ?? 0)
                  + (majorInputToMinor(tax || "0", currency) ?? 0) <= 0
              }
              onClick={() => void save()}
            >
              {ar ? "حفظ" : "Save"}
            </Button>
          </Inline>
        }
      >
        <Stack>
          <Grid columns={2}>
            <SelectField
              label={ar ? "الفئة" : "Category"}
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
            >
              <option value="">
                {ar ? "اختر الفئة" : "Select category"}
              </option>
              {categories
                .filter((category) => category.isActive)
                .map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.expenseAccountCode} ·
                    {" "}
                    {ar ? category.nameAr : category.nameEn}
                  </option>
                ))}
            </SelectField>
            <Field
              label={ar ? "التاريخ" : "Date"}
              type="date"
              value={expenseDate}
              onChange={(event) => setExpenseDate(event.target.value)}
            />
            <Field
              label={ar ? "المورد" : "Vendor"}
              value={vendor}
              onChange={(event) => setVendor(event.target.value)}
            />
            <Field
              label={ar ? "رقم الفاتورة" : "Invoice number"}
              value={invoice}
              onChange={(event) => setInvoice(event.target.value)}
            />
            <Field
              label={ar ? "الصافي" : "Net amount"}
              type="number"
              min="0"
              step="0.01"
              value={net}
              onChange={(event) => setNet(event.target.value)}
            />
            <Field
              label={ar ? "الضريبة" : "Tax amount"}
              type="number"
              min="0"
              step="0.01"
              value={tax}
              onChange={(event) => setTax(event.target.value)}
            />
            <SelectField
              label={ar ? "طريقة الدفع" : "Payment method"}
              value={paymentMethod}
              onChange={(event) => setPaymentMethod(event.target.value)}
            >
              <option value="Cash">Cash</option>
              <option value="Card">Card</option>
              <option value="Account">Account</option>
            </SelectField>
            <Field
              label={ar ? "مرجع الدفع" : "Payment reference"}
              value={paymentReference}
              onChange={(event) => setPaymentReference(event.target.value)}
            />
          </Grid>
          <TextAreaField
            label={ar ? "الوصف" : "Description"}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
          <Alert
            tone="info"
            title={ar ? "الإجمالي" : "Total"}
          >
            {money(
              (majorInputToMinor(net || "0", currency) ?? 0)
                + (majorInputToMinor(tax || "0", currency) ?? 0),
              language,
              currency
            )}
          </Alert>
        </Stack>
      </Dialog>
    </Stack>
  );
}

function AccountsSection({
  language,
  currency,
  refreshKey,
  canManage,
  onChanged
}: {
  language: ReportsLanguage;
  currency: string;
  refreshKey: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const ar = language === "ar";
  const toast = useToast();
  const [accounts, setAccounts] = useState<FinanceAccount[]>([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [code, setCode] = useState("");
  const [nameAr, setNameAr] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [accountType, setAccountType] =
    useState<FinanceAccount["accountType"]>("Expense");
  const [normalBalance, setNormalBalance] =
    useState<FinanceAccount["normalBalance"]>("Debit");
  const [allowManual, setAllowManual] = useState(true);
  const [error, setError] = useState("");

  const load = async () => setAccounts(await getFinanceAccounts(currency));

  useEffect(() => {
    void load().catch((caught) => setError(errorMessage(caught)));
  }, [refreshKey]);

  const save = async () => {
    try {
      await saveFinanceAccount({
        code,
        nameAr,
        nameEn,
        accountType,
        normalBalance,
        currency,
        allowManualPosting: allowManual,
        isActive: true
      });
      setEditorOpen(false);
      setCode("");
      setNameAr("");
      setNameEn("");
      await load();
      onChanged();
      toast.notify({
        tone: "success",
        title: ar ? "تم إنشاء الحساب" : "Account created"
      });
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  return (
    <Stack>
      {error ? <ReportError language={language} error={error} /> : null}
      <Panel
        title={ar ? "دليل الحسابات" : "Chart of accounts"}
        actions={
          canManage ? (
            <Button
              data-testid="new-finance-account"
              onClick={() => setEditorOpen(true)}
            >
              {ar ? "حساب جديد" : "New account"}
            </Button>
          ) : null
        }
      >
        <div className="reports-dynamic-table-wrap">
          <table className="reports-dynamic-table">
            <thead>
              <tr>
                <th>{ar ? "الرمز" : "Code"}</th>
                <th>{ar ? "الحساب" : "Account"}</th>
                <th>{ar ? "النوع" : "Type"}</th>
                <th>{ar ? "الرصيد الطبيعي" : "Normal balance"}</th>
                <th>{ar ? "الرصيد الحالي" : "Current balance"}</th>
                <th>{ar ? "يدوي" : "Manual"}</th>
                <th>{ar ? "الحالة" : "Status"}</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id}>
                  <td><strong>{account.code}</strong></td>
                  <td>
                    {ar ? account.nameAr : account.nameEn}
                    {account.isSystem ? (
                      <small className="reports-cell-sub">
                        {ar ? "حساب نظام" : "System account"}
                      </small>
                    ) : null}
                  </td>
                  <td>{account.accountType}</td>
                  <td>{account.normalBalance}</td>
                  <td>{money(account.currentBalanceMinor, language, currency)}</td>
                  <td>
                    <StatusBadge
                      tone={account.allowManualPosting ? "info" : "neutral"}
                    >
                      {account.allowManualPosting
                        ? ar ? "مسموح" : "Allowed"
                        : ar ? "تلقائي" : "Automatic"}
                    </StatusBadge>
                  </td>
                  <td>
                    <StatusBadge
                      tone={account.isActive ? "success" : "warning"}
                    >
                      {account.isActive ? "Active" : "Inactive"}
                    </StatusBadge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Dialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={ar ? "حساب مالي جديد" : "New finance account"}
        footer={
          <Inline>
            <Button
              variant="secondary"
              onClick={() => setEditorOpen(false)}
            >
              {ar ? "إلغاء" : "Cancel"}
            </Button>
            <Button
              disabled={!code || !nameAr || !nameEn}
              onClick={() => void save()}
            >
              {ar ? "حفظ" : "Save"}
            </Button>
          </Inline>
        }
      >
        <Stack>
          <Grid columns={2}>
            <Field
              label={ar ? "الرمز" : "Code"}
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
            <SelectField
              label={ar ? "نوع الحساب" : "Account type"}
              value={accountType}
              onChange={(event) => setAccountType(
                event.target.value as FinanceAccount["accountType"]
              )}
            >
              {[
                "Asset",
                "Liability",
                "Equity",
                "Revenue",
                "ContraRevenue",
                "CostOfGoodsSold",
                "Expense"
              ].map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </SelectField>
            <Field
              label={ar ? "الاسم العربي" : "Arabic name"}
              value={nameAr}
              onChange={(event) => setNameAr(event.target.value)}
            />
            <Field
              label={ar ? "الاسم الإنجليزي" : "English name"}
              value={nameEn}
              onChange={(event) => setNameEn(event.target.value)}
            />
            <SelectField
              label={ar ? "الرصيد الطبيعي" : "Normal balance"}
              value={normalBalance}
              onChange={(event) => setNormalBalance(
                event.target.value as FinanceAccount["normalBalance"]
              )}
            >
              <option value="Debit">Debit</option>
              <option value="Credit">Credit</option>
            </SelectField>
            <Checkbox
              label={ar ? "السماح بالترحيل اليدوي" : "Allow manual posting"}
              checked={allowManual}
              onChange={(event) => setAllowManual(event.target.checked)}
            />
          </Grid>
        </Stack>
      </Dialog>
    </Stack>
  );
}

function PeriodsSection({
  language,
  refreshKey,
  canManage,
  canClose,
  onChanged
}: {
  language: ReportsLanguage;
  refreshKey: number;
  canManage: boolean;
  canClose: boolean;
  onChanged: () => void;
}) {
  const ar = language === "ar";
  const toast = useToast();
  const [periods, setPeriods] = useState<FinancialPeriod[]>([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [periodCode, setPeriodCode] = useState("");
  const [startsOn, setStartsOn] =
    useState(`${new Date().getUTCFullYear()}-01-01`);
  const [endsOn, setEndsOn] =
    useState(`${new Date().getUTCFullYear()}-12-31`);
  const [error, setError] = useState("");

  const load = async () => setPeriods(await getFinancialPeriods());

  useEffect(() => {
    void load().catch((caught) => setError(errorMessage(caught)));
  }, [refreshKey]);

  const save = async () => {
    try {
      await saveFinancialPeriod({
        periodCode,
        startsOn,
        endsOn
      });
      setEditorOpen(false);
      setPeriodCode("");
      await load();
      onChanged();
      toast.notify({
        tone: "success",
        title: ar ? "تم إنشاء الفترة" : "Period created"
      });
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const change = async (
    period: FinancialPeriod,
    status: FinancialPeriod["status"]
  ) => {
    try {
      await changeFinancialPeriodStatus(
        period,
        status,
        status === "Closed"
          ? "Period close from Finance Center"
          : "Period reopened from Finance Center"
      );
      await load();
      onChanged();
      toast.notify({
        tone: "success",
        title: ar ? "تم تحديث الفترة" : "Period updated"
      });
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  return (
    <Stack>
      {error ? <ReportError language={language} error={error} /> : null}
      <Panel
        title={ar ? "الفترات المالية" : "Financial periods"}
        actions={
          canManage ? (
            <Button
              data-testid="new-financial-period"
              onClick={() => setEditorOpen(true)}
            >
              {ar ? "فترة جديدة" : "New period"}
            </Button>
          ) : null
        }
      >
        <div className="reports-period-grid">
          {periods.map((period) => (
            <Card key={period.id} className="reports-period-card">
              <div className="reports-period-card__header">
                <div>
                  <strong>{period.periodCode}</strong>
                  <small>{period.branchName}</small>
                </div>
                <StatusBadge tone={statusTone(period.status)}>
                  {period.status}
                </StatusBadge>
              </div>
              <div className="reports-period-card__dates">
                <span>
                  {ar ? "البداية" : "Starts"}
                  <strong>{dateLabel(period.startsOn, language)}</strong>
                </span>
                <span>
                  {ar ? "النهاية" : "Ends"}
                  <strong>{dateLabel(period.endsOn, language)}</strong>
                </span>
              </div>
              {period.closedAt ? (
                <small>
                  {ar ? "أغلقت" : "Closed"}:
                  {" "}
                  {dateTimeLabel(period.closedAt, language)}
                </small>
              ) : null}
              {canClose ? (
                <Inline>
                  {period.status !== "Closed" ? (
                    <Button
                      size="sm"
                      variant="danger"
                      data-testid={`close-period-${period.id}`}
                      onClick={() => void change(period, "Closed")}
                    >
                      {ar ? "إغلاق" : "Close"}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => void change(period, "Reopened")}
                    >
                      {ar ? "إعادة فتح" : "Reopen"}
                    </Button>
                  )}
                </Inline>
              ) : null}
            </Card>
          ))}
        </div>
      </Panel>

      <Dialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={ar ? "فترة مالية جديدة" : "New financial period"}
        footer={
          <Inline>
            <Button
              variant="secondary"
              onClick={() => setEditorOpen(false)}
            >
              {ar ? "إلغاء" : "Cancel"}
            </Button>
            <Button
              disabled={!periodCode || !startsOn || !endsOn}
              onClick={() => void save()}
            >
              {ar ? "حفظ" : "Save"}
            </Button>
          </Inline>
        }
      >
        <Grid columns={2}>
          <Field
            label={ar ? "رمز الفترة" : "Period code"}
            value={periodCode}
            onChange={(event) => setPeriodCode(event.target.value)}
          />
          <span />
          <Field
            label={ar ? "من" : "Starts on"}
            type="date"
            value={startsOn}
            onChange={(event) => setStartsOn(event.target.value)}
          />
          <Field
            label={ar ? "إلى" : "Ends on"}
            type="date"
            value={endsOn}
            onChange={(event) => setEndsOn(event.target.value)}
          />
        </Grid>
      </Dialog>
    </Stack>
  );
}

export function FinanceRebuildAction({
  language,
  scope,
  onChanged
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  onChanged: () => void;
}) {
  const ar = language === "ar";
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const rebuild = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await rebuildFinancePostings(scope);
      toast.notify({
        tone: "success",
        title: ar ? "اكتملت إعادة بناء القيود" : "Finance postings rebuilt",
        message: ar
          ? `${result.salesEntriesCreated} مبيعات · ${result.refundEntriesCreated} مرتجعات · ${result.purchaseEntriesCreated} استلامات`
          : `${result.salesEntriesCreated} sales · ${result.refundEntriesCreated} refunds · ${result.purchaseEntriesCreated} receipts`
      });
      onChanged();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack>
      {error ? <ReportError language={language} error={error} /> : null}
      <Button
        variant="secondary"
        icon="activity"
        disabled={busy}
        onClick={() => void rebuild()}
      >
        {busy
          ? ar ? "جارٍ إعادة البناء" : "Rebuilding"
          : ar ? "إعادة بناء القيود من العمليات" : "Rebuild source postings"}
      </Button>
    </Stack>
  );
}
